// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';

// --- RxJs
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { LanguagesBaseService } from './languages-base.service';
import { environment } from '../../../../environments/environment';
import { AppService } from '../app.service';
import { ApplicationError, Constants, LanguageModel } from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class LanguagesService extends LanguagesBaseService {
  private readonly _SELECTED_LANGUAGE_KEY: string = 'hs:pe:selectedLanguage';
  private readonly _NOT_SET: number = -1;

  private _isInitialised: boolean;
  private _initialisedLanguageId: number;

  constructor(
    private httpClient: HttpClient,
    private appService: AppService,
    private translate: TranslateService
  ) {
    super();
    this._isInitialised = false;
    this._initialisedLanguageId = this._NOT_SET;
    const selectedLanguage = (localStorage.getItem(this._SELECTED_LANGUAGE_KEY) !== "undefined") ? JSON.parse(
      localStorage.getItem(this._SELECTED_LANGUAGE_KEY)
    ) : null;
  }

  initialiseLanguage(selectedLanguageId: number): void {
    if (this._isInitialised) {
      return;
    }

    const savedLanguage = JSON.parse(
      localStorage.getItem(this._SELECTED_LANGUAGE_KEY)
    );
    if (savedLanguage) {
      this._isInitialised = true;
      this._initialisedLanguageId = savedLanguage.id;
      this._selectedLanguage$.next(savedLanguage);

      //this._flagLanguageListItemAsSelected(selectedLanguageId.toString());

      return;
    }

    this.languages$.subscribe(languages => {
      if (languages) {
        const selectedLanguage = languages.find(l => {
          return l.id === selectedLanguageId;
        });
        if (selectedLanguageId) {
          this._isInitialised = true;
          localStorage.setItem(
            this._SELECTED_LANGUAGE_KEY,
            JSON.stringify(selectedLanguage)
          );
          this._selectedLanguage$.next(selectedLanguage);
        }
      }
    });
  }

  private _flagLanguageListItemAsSelected(selectedValue: string) {
    if (this._languagesList$.value) {
      const languagesList = this._languagesList$.value;
      languagesList.forEach(l => l.isSelected = l.value === selectedValue);
      this._languagesList$.next(languagesList);
    }
  }

  setSelectedLanguage(selectedLanguage: LanguageModel): void {
    localStorage.setItem(
      this._SELECTED_LANGUAGE_KEY,
      JSON.stringify(selectedLanguage)
    );
    //this._flagLanguageListItemAsSelected(selectedLanguage.id.toString());
  }

  loadLanguages(): void {
    const uri =
      environment.apiConfig.baseUrl + environment.apiConfig.languagesApi;
    this.appService.setLoading(true);
    this.httpClient
      .get<LanguageModel[]>(uri)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe(languages => {
        this.appService.setLoading(false);
        this._languages$.next(languages);
        this._processLanguages(languages);
        if (!this._isInitialised && (this._initialisedLanguageId !== this._NOT_SET)) {
          this.initialiseLanguage(this._initialisedLanguageId);
        }
      });
  }

  private handleError(err) {
    this.appService.setLoading(false);
    console.error(err);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage,
        originalError: err
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);

    return of([Constants.DEFAULT_LANGUAGE] as LanguageModel[]);
  }
}
